import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Plus, Server as ServerIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { serversApi } from '../api.js';
import { NovaContaModal } from '../contas-old/NovaContaModal.js';
import { ContaDetailPanel } from '../contas-old/ContaDetailPanel.js';
import { EditarContaModal } from '../contas-old/EditarContaModal.js';
import { ResetSenhaModal } from '../contas-old/ResetSenhaModal.js';
import { setSelectedServer } from '../contas-old/api.js';
import type { Conta as OldConta } from '../contas-old/types.js';

const STATUS_SUSPENSOS = ['suspended', 'cancelled'];

export function ServerDetail() {
  const { id } = useParams({ from: '/authLayout/servidores/$id' });
  const serverId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [detailConta, setDetailConta] = useState<OldConta | null>(null);
  const [editConta, setEditConta] = useState<OldConta | null>(null);
  const [resetConta, setResetConta] = useState<OldConta | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  // O shim de api dos componentes portados precisa saber qual servidor é o alvo.
  useEffect(() => {
    setSelectedServer(serverId);
    return () => setSelectedServer(null);
  }, [serverId]);

  // Fecha o menu ⋮ com Escape.
  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openMenu]);

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
  });
  const server = serversQuery.data?.find((s) => s.id === serverId);

  const healthQuery = useQuery({
    queryKey: ['server-health', serverId],
    queryFn: () => serversApi.health(serverId),
    retry: false,
    enabled: !!server,
  });

  const contasQuery = useQuery({
    queryKey: ['contas', serverId],
    queryFn: () => serversApi.contas(serverId),
    enabled: !!server,
  });

  if (serversQuery.isLoading) {
    return <div className="py-12 text-center text-sm text-[#94a3b8]">Carregando...</div>;
  }

  if (!server) {
    return (
      <Card className="p-12 text-center">
        <h3 className="text-base font-semibold text-[#0f172a]">Servidor não encontrado</h3>
        <Button className="mt-4" variant="outline" onClick={() => navigate({ to: '/' })}>
          ← Voltar
        </Button>
      </Card>
    );
  }

  const contas = contasQuery.data ?? [];
  const filtered = contas.filter(
    (c) =>
      c.nome.toLowerCase().includes(search.toLowerCase()) ||
      c.email.toLowerCase().includes(search.toLowerCase()),
  );
  const isUp = healthQuery.data?.status === 200;
  const counters = healthQuery.data?.counters;

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate({ to: '/' })}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-[#64748b] hover:text-[#0f172a] transition-colors"
        >
          <ArrowLeft size={14} /> Servidores
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#eff4ff] text-[#0a4ee3]">
              <ServerIcon size={24} strokeWidth={2} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#0f172a]">{server.name}</h1>
              <div className="mt-0.5 flex items-center gap-3 text-sm text-[#64748b]">
                <span className="font-mono text-xs">{server.url}</span>
                <span
                  className={
                    isUp
                      ? 'inline-flex px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider bg-emerald-50 text-emerald-700'
                      : 'inline-flex px-2 py-0.5 rounded text-[10px] font-semibold tracking-wider bg-red-50 text-red-600'
                  }
                >
                  {isUp ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} disabled={!isUp}>
            <Plus size={14} /> Criar Conta
          </Button>
        </div>
      </div>

      {/* Counters */}
      {counters && (
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">Contas</div>
            <div className="mt-1 text-[28px] font-bold tabular-nums text-[#0f172a]">{counters.contas_total}</div>
            <div className="text-xs text-[#64748b]">{counters.contas_ativas} ativas</div>
          </div>
          <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">Empresas</div>
            <div className="mt-1 text-[28px] font-bold tabular-nums text-[#0f172a]">{counters.empresas_total}</div>
            <div className="text-xs text-[#64748b]">{counters.empresas_ativas} ativas</div>
          </div>
          <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">Usuários ativos</div>
            <div className="mt-1 text-[28px] font-bold tabular-nums text-[#0f172a]">{counters.usuarios_ativos}</div>
            <div className="text-xs text-[#64748b]">total no servidor</div>
          </div>
        </div>
      )}

      {/* Busca + contador */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm text-[#64748b]">
          {contas.length} conta{contas.length !== 1 ? 's' : ''} cadastrada
          {contas.length !== 1 ? 's' : ''}
        </p>
        <div className="relative w-full max-w-[320px]">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou e-mail…"
            className="w-full rounded-lg border border-[#e2e8f0] bg-white pl-9 pr-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none focus:ring-2 focus:ring-[#0a4ee3]/20 focus:border-[#0a4ee3]"
          />
        </div>
      </div>

      {/* Tabela de contas */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] shadow-sm overflow-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="sticky top-0 z-10 bg-[#fafafa]">
            <tr className="border-b border-[#f1f5f9]">
              <th className="px-4 py-3 text-left font-semibold text-[#374151]">Nome</th>
              <th className="px-4 py-3 text-left font-semibold text-[#374151]">Email</th>
              <th className="px-4 py-3 text-left font-semibold text-[#374151]">Plano</th>
              <th className="px-4 py-3 text-left font-semibold text-[#374151]">Status</th>
              <th className="px-4 py-3 text-left font-semibold text-[#374151] whitespace-nowrap">Empresas</th>
              <th className="px-4 py-3 text-left font-semibold text-[#374151] whitespace-nowrap">Criada em</th>
              <th className="px-4 py-3 text-right font-semibold text-[#374151]">Ações</th>
            </tr>
          </thead>
          <tbody>
            {contasQuery.isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#94a3b8]">
                  Carregando contas...
                </td>
              </tr>
            )}
            {!contasQuery.isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-sm text-[#94a3b8]">
                  {contas.length === 0
                    ? 'Nenhuma conta ainda. Clique em "Criar Conta" pra adicionar a primeira.'
                    : 'Nenhuma conta encontrada para a busca.'}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const suspended = STATUS_SUSPENSOS.includes(c.status);
              return (
                <tr
                  key={c.id}
                  className="group border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] transition-colors"
                >
                  <td className="px-4 py-3.5">
                    <span className="font-medium text-[#0f172a]">{c.nome}</span>
                  </td>
                  <td className="px-4 py-3.5 text-[#64748b] text-xs">{c.email}</td>
                  <td className="px-4 py-3.5 text-[#64748b] text-xs capitalize">{c.plan}</td>
                  <td className="px-4 py-3.5">
                    <span
                      className={
                        suspended
                          ? 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-600'
                          : 'inline-flex px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700'
                      }
                    >
                      {c.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-xs">
                    <span className="font-medium text-[#374151] tabular-nums">{c.total_empresas}</span>
                  </td>
                  <td className="px-4 py-3.5 text-[#94a3b8] text-xs whitespace-nowrap">
                    {new Date(c.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openMenu === c.id) {
                            setOpenMenu(null);
                            return;
                          }
                          const rect = e.currentTarget.getBoundingClientRect();
                          setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                          setOpenMenu(c.id);
                        }}
                        className="p-1.5 rounded-lg text-[#94a3b8] hover:text-[#64748b] hover:bg-[#f1f5f9] transition-colors"
                        title="Mais opções"
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="5" r="1" fill="currentColor" />
                          <circle cx="12" cy="12" r="1" fill="currentColor" />
                          <circle cx="12" cy="19" r="1" fill="currentColor" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dropdown ⋮ (portal) — Ver detalhes / Alterar planos e extras / Redefinir senha */}
      {openMenu &&
        createPortal(
          (() => {
            const conta = contas.find((c) => c.id === openMenu);
            if (!conta) return null;
            const oc = conta as unknown as OldConta;
            return (
              <>
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 9998 }}
                  onClick={() => setOpenMenu(null)}
                />
                <div
                  style={{
                    position: 'fixed',
                    top: menuPos.top,
                    right: menuPos.right,
                    zIndex: 9999,
                    width: 230,
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 12,
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    padding: '4px 0',
                    fontSize: 14,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setDetailConta(oc);
                      setOpenMenu(null);
                    }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-[#374151] hover:bg-[#eff4ff] hover:text-[#0a4ee3] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    Ver detalhes
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditConta(oc);
                      setOpenMenu(null);
                    }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-[#374151] hover:bg-[#eff4ff] hover:text-[#0a4ee3] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 20h9" />
                      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                    </svg>
                    Alterar planos e extras
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setResetConta(oc);
                      setOpenMenu(null);
                    }}
                    className="w-full text-left flex items-center gap-2 px-4 py-2.5 text-[#374151] hover:bg-[#eff4ff] hover:text-[#0a4ee3] transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Redefinir senha
                  </button>
                </div>
              </>
            );
          })(),
          document.body,
        )}

      <NovaContaModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          void qc.invalidateQueries({ queryKey: ['contas', serverId] });
          void qc.invalidateQueries({ queryKey: ['server-health', serverId] });
        }}
      />

      {detailConta && (
        <ContaDetailPanel
          conta={detailConta}
          onClose={() => setDetailConta(null)}
          onEdit={(c) => setEditConta(c)}
          onRefresh={() => {
            void qc.invalidateQueries({ queryKey: ['contas', serverId] });
            void qc.invalidateQueries({ queryKey: ['server-health', serverId] });
          }}
        />
      )}

      {editConta && (
        <EditarContaModal
          open={!!editConta}
          onClose={() => setEditConta(null)}
          onSaved={() => {
            void qc.invalidateQueries({ queryKey: ['contas', serverId] });
            void qc.invalidateQueries({ queryKey: ['server-health', serverId] });
          }}
          conta={editConta}
        />
      )}

      {resetConta && (
        <ResetSenhaModal
          open={!!resetConta}
          onClose={() => setResetConta(null)}
          conta={resetConta}
        />
      )}
    </div>
  );
}
